import { EditIconButton, LookupOption, notify } from '@/components';
import { useMemo, useState } from 'react';
import {
  SpecialRequestState,
  UpdateSpecialRequestArgs,
  useUpdateSpecialRequestMutation,
} from '@/redux';
import { useTranslation } from 'react-i18next';
import { SpecialRequestDetailsModal } from './SpecialRequestDetailsModal';
import { SpecialRequestCommentsModal } from './SpecialRequestCommentsModal';
import { useCommunityContext } from '@/views/Communities.Common';
import { CodedException, CommunityPermissions } from '@/core';
import { RtkqNotifyError } from '@/redux/rtkq';

interface Props {
  stepId: string;
  projectId: string;
  homeId: string;
  value: SpecialRequestState;
  disabledCategory?: boolean;
  isTableAction?: boolean;
}

function useFormValue(value: SpecialRequestState | undefined) {
  return useMemo<UpdateSpecialRequestArgs | undefined>(
    () =>
      value
        ? {
            id: value.id,
            stepId: value.step.id,
            homeId: value.homeId,
            projectId: value.projectId,
            categoryId: value.category.category.id,
            name: value.name,
            status: value.status,
            description: value.description,
            constructionDescription: value.constructionDescription,
            isFloorplan: value.isFloorplan,
            roomId: value.room?.id,
            fee: value.fee,
          }
        : undefined,
    [value],
  );
}

export const EditSpecialRequestButton = (props: Props) => {
  const { value, disabledCategory, isTableAction } = props;
  const { modal, open } = useEditSpecialRequestAction(disabledCategory);

  return (
    <>
      <EditIconButton
        className={isTableAction ? 'table-action' : undefined}
        onClick={() => open(value)}
      />
      {modal}
    </>
  );
};

export const handleEditSpecialRequestError = (error: any) => {
  const e = CodedException.from(error);
  if (e.code === 'INV.PROJECT_STEP.PASSED') {
    notify.error.coded({
      ...e,
      code: 'INV.PROJECT_STEP.SELECTED_IS_PASSED',
    });
    return;
  }

  if (e.code === 'INV.PROJECT_STEP.FINALIZED') {
    notify.error.coded({
      ...e,
      code: 'INV.PROJECT_STEP.FINALIZED',
    });
    return;
  }

  throw error;
};

const useSubmit = (onClose: () => any) => {
  const [update] = useUpdateSpecialRequestMutation();

  return (args: UpdateSpecialRequestArgs) => {
    update({ ...args, [RtkqNotifyError]: false })
      .unwrap()
      .then(() => {
        notify.success.t('specialRequests.saved');
        onClose();
      })
      .catch(handleEditSpecialRequestError);
  };
};

export function useEditSpecialRequestAction(disabledCategory?: boolean) {
  const [item, setItem] = useState<SpecialRequestState>();
  const formValue = useFormValue(item);
  const { t } = useTranslation();
  const submit = useSubmit(() => setItem(undefined));

  const preloadValue: LookupOption = useMemo(
    () => ({
      label: item?.category.category.name ?? '',
      value: item?.category.category.id ?? '',
    }),
    [item?.category],
  );

  const { assert } = useCommunityContext();

  const isEditingEnabled = assert(
    CommunityPermissions.Personalization.Manage,
    true,
  );

  return {
    open: setItem,
    modal:
      item &&
      (!isEditingEnabled ? (
        <SpecialRequestCommentsModal
          title={t('specialRequests.addComments')}
          onClose={() => setItem(undefined)}
          value={formValue}
        />
      ) : (
        <SpecialRequestDetailsModal
          stepId={item.step.id}
          onClose={() => setItem(undefined)}
          onSubmit={submit}
          title={t('specialRequests.editTitle')}
          value={formValue}
          isEdit
          preloadValue={preloadValue}
          disabledCategory={disabledCategory}
          {...item}
        />
      )),
  };
}
